import { useEffect, useRef } from "react";
import {
  Banner,
  SymptomsSection,
  AskAnswerActSection,
  SmartFeatures,
  KeyBenefitsSection,
  DataSecuritySection,
  HowItWorksSection,
  FAQSection,
  GetStartedSection,
  OurVisionSection,
} from "../Home/sections/index";
import { PricingSection } from './sections';

const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean;
  return function(this: any) {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const LPSharesPage = () => {
  const askAnswerActRef = useRef<HTMLElement>(null);
  const smartFeaturesRef = useRef<HTMLElement>(null);
  const keyBenefitsRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);
  const ourVisionRef = useRef<HTMLElement>(null);

  const targetSectionRefs = [
    askAnswerActRef,
    smartFeaturesRef,
    pricingRef,
    ourVisionRef,
  ];

  useEffect(() => {
    const applyTransform = (
      element: HTMLElement,
      progress: number // A value typically between 0 and 1, where 0 is reset and 1 is fully transformed
    ) => {
      const targetScaleX = 0.95;
      const targetTranslateY = 30;
      const perspectiveValue = 1200;

      const currentScaleX = 1 + (targetScaleX - 1) * progress;
      const currentTranslateY = targetTranslateY * progress;

      element.style.transform = `perspective(${perspectiveValue}px) translateY(${currentTranslateY}px) scaleX(${currentScaleX})`;
    };

    const handleScroll = throttle(() => {
      if (document.body.clientWidth < 639) {
        targetSectionRefs.forEach((ref) => {
          const section = ref.current;
          if (section && section.previousElementSibling && section.previousElementSibling.tagName === 'SECTION') {
            (section.previousElementSibling as HTMLElement).style.transform = 'none';
          }
        });
        return;
      }

      targetSectionRefs.forEach((sectionRef) => {
        const currentSection = sectionRef.current;
        if (!currentSection) return;

        const prevSection = currentSection.previousElementSibling as HTMLElement;

        if (prevSection && prevSection.tagName === "SECTION") {
          const sectionRect = currentSection.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          const startScrollRatio = 0.8;
          const endScrollRatio = 0.2;

          const startPoint = viewportHeight * startScrollRatio;
          const endPoint = viewportHeight * endScrollRatio;

          let progress = 1 - (sectionRect.top - endPoint) / (startPoint - endPoint);
          progress = Math.max(0, Math.min(1, progress));

          if (progress > 0 && progress < 1) {
            // Section is within the active scroll range, apply interpolated transform
            applyTransform(prevSection, progress);
          } else if (progress === 0) {
            // Section is below the start point (not yet in animation range)
            applyTransform(prevSection, 0); // Reset to default
          } else if (progress === 1) {
            // Section has scrolled completely past the end point (component "ends")
            // Apply the full transformation to make the effect persist
            applyTransform(prevSection, 1);
          }
        }
      });
    }, 16);

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      targetSectionRefs.forEach((ref) => {
        const section = ref.current;
        if (section && section.previousElementSibling && section.previousElementSibling.tagName === 'SECTION') {
           (section.previousElementSibling as HTMLElement).style.transform = 'none';
        }
      });
    };
  }, []);

  return (
    <div className="flex flex-col bg-[#0E0E0E]">
      <div className="bg-black relative">
        <Banner />
      </div>

      <SymptomsSection />
      <AskAnswerActSection ref={askAnswerActRef} />
      <SmartFeatures ref={smartFeaturesRef} />
      <HowItWorksSection />
      <KeyBenefitsSection ref={keyBenefitsRef} />
      <DataSecuritySection />
      <PricingSection ref={pricingRef} />
      <OurVisionSection ref={ourVisionRef} />
      <FAQSection />
      <GetStartedSection />
    </div>
  );
};